/**
 * Shopping Cart Page
 * Full cart view with checkout option
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import CartItem from '../components/Cart/CartItem';
import Breadcrumbs from '../components/UI/Breadcrumbs';
import { formatPrice } from '../utils';

const Cart: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { items, itemCount, totalAmount, updateQuantity, removeFromCart, clearCart } = useCart();
  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);

  const deliveryFee = totalAmount > 2000 ? 0 : 200; // Free delivery over KSh 2000
  const finalTotal = totalAmount + deliveryFee - discount;

  const handleApplyPromo = () => {
    // TODO: Implement promo code validation with backend
    if (promoCode.toLowerCase() === 'welcome10') {
      const discountAmount = totalAmount * 0.1;
      setDiscount(discountAmount);
      toast.success('Promo code applied! 10% discount');
    } else if (promoCode) {
      toast.error('Invalid promo code');
    }
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      clearCart();
      toast.info('Cart cleared');
    }
  };

  const handleCheckout = () => {
    if (items.length === 0) {
      toast.warning('Your cart is empty!');
      return;
    }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[
            { label: 'Home', path: '/' },
            { label: 'Cart' },
          ]} />

          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            <span className="text-8xl block mb-6">🛒</span>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Looks like you haven't added any products yet. Start shopping to fill your cart with fresh produce!
            </p>
            <Link
              to="/products"
              className="inline-block px-8 py-4 bg-gradient-to-r from-green-600 to-green-800 text-white rounded-lg hover:from-green-700 hover:to-green-900 transition-all font-bold shadow-lg"
            >
              Browse Products
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={[
          { label: 'Home', path: '/' },
          { label: 'Cart' },
        ]} />

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Shopping Cart</h1>
            <p className="text-gray-600 mt-2">{itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart</p>
          </div>
          <button
            onClick={handleClearCart}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-all font-medium"
          >
            Clear Cart
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map(item => (
              <CartItem
                key={item.product._id}
                item={item}
                onUpdateQuantity={updateQuantity}
                onRemove={removeFromCart}
              />
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Order Summary</h2>

              {/* Summary Details */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between text-gray-700">
                  <span>Subtotal ({itemCount} items)</span>
                  <span className="font-medium">{formatPrice(totalAmount)}</span>
                </div>

                <div className="flex items-center justify-between text-gray-700">
                  <span>Delivery Fee</span>
                  <span className="font-medium">
                    {deliveryFee === 0 ? (
                      <span className="text-green-600">FREE</span>
                    ) : (
                      formatPrice(deliveryFee)
                    )}
                  </span>
                </div>

                {discount > 0 && (
                  <div className="flex items-center justify-between text-green-600">
                    <span>Discount</span>
                    <span className="font-medium">-{formatPrice(discount)}</span>
                  </div>
                )}

                {totalAmount < 2000 && (
                  <div className="bg-blue-50 text-blue-700 p-3 rounded-lg text-xs">
                    💡 Add {formatPrice(2000 - totalAmount)} more for FREE delivery!
                  </div>
                )}
              </div>

              {/* Promo Code */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Promo Code
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value)}
                    placeholder="Enter code"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    onClick={handleApplyPromo}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all font-medium"
                  >
                    Apply
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex items-center justify-between text-xl font-bold">
                  <span className="text-gray-900">Total</span>
                  <span className="text-green-600">{formatPrice(finalTotal)}</span>
                </div>
              </div>

              {/* Checkout Button */}
              <button
                onClick={handleCheckout}
                className="w-full py-4 bg-gradient-to-r from-green-600 to-green-800 text-white rounded-xl hover:from-green-700 hover:to-green-900 transition-all font-bold shadow-lg text-lg"
              >
                Proceed to Checkout
              </button>

              {/* Continue Shopping */}
              <Link
                to="/products"
                className="block w-full py-3 text-center text-green-600 hover:text-green-700 transition-all font-medium mt-3"
              >
                ← Continue Shopping
              </Link>

              {/* Security Badges */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                  <div className="flex items-center space-x-1">
                    <span>🔒</span>
                    <span>Secure</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>📱</span>
                    <span>M-Pesa</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span>✓</span>
                    <span>Verified</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recommended Products */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">You may also like</h2>
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-200">
            <p className="text-center text-gray-500">
              Recommended products coming soon...
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;

